import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { aiService } from '../../services/AIService';
import { PRICING_TIERS, type UsageTier } from '../../services/PricingService';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { SparklesIcon, CheckIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';

export const UsageAlert: React.FC = () => {
  const { user } = useAuth();
  const [showUpgrade, setShowUpgrade] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [additionalUsage, setAdditionalUsage] = React.useState(1); // Default $1 additional

  const stats = user ? aiService.getUsageStats() : null;
  const remaining = user ? aiService.getRemainingUsage() : 0;
  const currentTier = stats?.tier || 'Free';
  const currentTierData = PRICING_TIERS.find(t => t.name === currentTier);

  const handleUpgrade = async () => {
    if (!user) return;
    
    setIsProcessing(true);
    try {
      // Here you would integrate with your payment processor
      const success = await aiService.upgradeTier('Enterprise');
      if (success) {
        setShowUpgrade(false);
      }
    } catch (error) {
      console.error('Error upgrading tier:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleIncreaseLimit = async () => {
    if (!user) return;
    
    setIsProcessing(true);
    try {
      // Here you would integrate with your payment processor
      const success = await aiService.increaseUsageLimit(user.id, additionalUsage);
      if (success) {
        setShowUpgrade(false);
      }
    } catch (error) {
      console.error('Error increasing limit:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!stats || !user) return null;

  // Only show alert for Enterprise users approaching limit
  if (currentTier === 'Enterprise') {
    const usagePercentage = ((stats.totalCost / (remaining + stats.totalCost)) * 100);
    const isLowUsage = remaining < 0.50; // Less than 50 cents remaining

    if (isLowUsage) {
      return (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-4 left-4 p-4 bg-card rounded-xl border shadow-lg max-w-sm"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <SparklesIcon className="h-5 w-5 text-yellow-500" />
              </div>
              <div className="space-y-1">
                <h3 className="font-medium">AI Usage Limit Approaching</h3>
                <p className="text-sm text-muted-foreground">
                  You have ${remaining.toFixed(2)} remaining in your Enterprise plan.
                </p>
                <Progress value={usagePercentage} className="h-1 mt-2" />
                <Button
                  onClick={() => setShowUpgrade(true)}
                  className="w-full mt-3"
                >
                  Increase Limit
                </Button>
              </div>
            </div>
          </motion.div>

          <Dialog open={showUpgrade} onOpenChange={setShowUpgrade}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Increase AI Usage Limit</DialogTitle>
                <DialogDescription>
                  Add more AI usage to your Enterprise plan
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Additional Usage Amount ($)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={additionalUsage}
                    onChange={(e) => setAdditionalUsage(Number(e.target.value))}
                    className="w-full"
                  />
                  <p className="text-sm text-muted-foreground">
                    Monthly cost: ${(9.99 + (additionalUsage * 3.33)).toFixed(2)}
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">What's included:</h4>
                  <ul className="space-y-2">
                    <li className="text-sm flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-primary" />
                      Current Enterprise features
                    </li>
                    <li className="text-sm flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-primary" />
                      ${additionalUsage.toFixed(2)} additional AI usage
                    </li>
                    <li className="text-sm flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-primary" />
                      Priority support
                    </li>
                  </ul>
                </div>
              </div>

              <DialogFooter>
                <Button
                  onClick={handleIncreaseLimit}
                  disabled={isProcessing}
                  className="w-full"
                >
                  Upgrade Now
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      );
    }
  } else if (currentTier === 'Free') {
    // Show upgrade prompt for Free users trying to use AI features
    return (
      <Dialog open={showUpgrade} onOpenChange={setShowUpgrade}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upgrade to Enterprise</DialogTitle>
            <DialogDescription>
              Access AI-powered features and insights
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Enterprise Plan Features:</h4>
              <ul className="space-y-2">
                {PRICING_TIERS[1].features.map((feature, i) => (
                  <li key={i} className="text-sm flex items-center gap-2">
                    <CheckIcon className="h-4 w-4 text-primary" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-muted p-4">
              <div className="text-2xl font-bold">
                ${PRICING_TIERS[1].pricePerMonth}
                <span className="text-sm font-normal text-muted-foreground">
                  /month
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Includes ${PRICING_TIERS[1].maxCost} of AI usage
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={handleUpgrade}
              disabled={isProcessing}
              className="w-full"
            >
              Upgrade to Enterprise
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return null;
}; 